/**
 * Iter88zbq (2026-02-19) — Unified Project Pipeline + ⌘K Search.
 * Iter88zbr (2026-02-20) — Sortierbare Spalten (Kunde · Status · Typ · Betrag · Datum).
 *
 * Rep-facing Fast-Access-Zeile oberhalb der Dashboard-Kacheln:
 *
 *   [🔍 Suche · Cmd+K]                          [+ Neu ▾]
 *   Chips: Alle · PV · WP · Gewonnen · Nachfassen · Vor-Ort
 *   Tabelle mit klickbaren Spalten-Headers (↑↓)
 *
 * Ein Klick auf einen Spalten-Header sortiert; ein zweiter Klick dreht die
 * Richtung; ein dritter Klick entfernt die Sortierung. Default-Sort ist
 * „updated_at desc" (zuletzt aktiv oben).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Sun, Flame, ArrowRight, X, ArrowUp, ArrowDown, ArrowUpDown, UserRound, ChevronDown } from "lucide-react";
import { eur0, isWon, monthKey, netOf, ownerCount, ownerKey, ownerLabel } from "./pipeline/pipelineUtils";
import { MonthSelect } from "./pipeline/MonthSelect";
import { RepRanking } from "./pipeline/RepRanking";

const STATUS_COLORS = {
  "Erstkontakt": "bg-zinc-700",
  "Termin geplant": "bg-blue-600",
  "Vor-Ort-Aufnahme": "bg-indigo-600",
  "Technische Prüfung": "bg-violet-600",
  "Angebot erstellt": "bg-amber-600",
  "Angebot versendet": "bg-orange-600",
  "Nachfassen": "bg-pink-600",
  "Gewonnen": "bg-[#00E575] text-[#09090B]",
  "Verloren": "bg-red-700",
  "Archiviert": "bg-zinc-600",
};

const CHIPS = [
  { id: "all", label: "Alle" },
  { id: "pv", label: "PV" },
  { id: "wp", label: "WP" },
  { id: "won", label: "Gewonnen" },
  { id: "followup", label: "Nachfassen" },
  { id: "onsite", label: "Vor-Ort" },
];

// Produktlinien: aus aktiven Angeboten (offer_lines), sonst aus Projekt-Typ
function _lines(p) {
  if (p.offer_lines?.length) return p.offer_lines;
  const k = (p.project_kind || p.type || "").toLowerCase();
  return [k === "pv" || p.pv_config_id ? "pv" : "wp"];
}
const _isPv = (p) => _lines(p).includes("pv");
const _isWp = (p) => _lines(p).includes("wp");

function _matchesChip(p, chip) {
  if (chip === "all") return true;
  if (chip === "pv") return _isPv(p);
  if (chip === "wp") return _isWp(p);
  if (chip === "won") return isWon(p);
  if (chip === "followup") return p.status === "Nachfassen";
  if (chip === "onsite") return p.status === "Vor-Ort-Aufnahme" || p.status === "Termin geplant";
  return true;
}

// Iter88zbr — Sortier-Getter je Spalten-ID. Vergleicht auf normalisierter
// Grundlage (lowercase Strings, Zahlen als Number, ISO-Datum lexikographisch).
const SORT_GETTERS = {
  customer: (p) => (p.customer_name || "").toLowerCase(),
  owner: (p) => (p.owner_name || "").toLowerCase(),
  status: (p) => p.status || "",
  type: (p) => _lines(p).join("+"),
  amount: (p) => Number(p.offers_total_net || 0),
  updated: (p) => p.updated_at || p.created_at || "",
};

const SORT_COLUMNS = [
  { id: "customer", label: "Kunde · Ort" },
  { id: "owner",    label: "Vertriebler" },
  { id: "status",   label: "Status" },
  { id: "type",     label: "Typ" },
  { id: "amount",   label: "Netto", align: "right" },
  { id: "updated",  label: "Datum",  align: "right" },
];
const GRID = "sm:grid-cols-[1fr_140px_130px_90px_110px_90px_32px]";
const GRID_SINGLE = "sm:grid-cols-[1fr_130px_90px_110px_90px_32px]";

// „Marek: 12 Projekte · 340.000 € netto · davon gewonnen 120.000 €“ – Basis: gefilterte Liste.
function PipelineTotals({ rows, ownerName }) {
  const total = rows.reduce((s, p) => s + netOf(p), 0);
  const won = rows.filter(isWon).reduce((s, p) => s + netOf(p), 0);
  return (
    <span className="text-[11.5px] text-zinc-400 ml-1 inline-flex items-center gap-1.5 flex-wrap" data-testid="pipeline-totals">
      {ownerName && <b className="text-zinc-200" data-testid="pipeline-totals-owner">{ownerName}:</b>}
      <span data-testid="pipeline-hit-count">{rows.length} {rows.length === 1 ? "Projekt" : "Projekte"}</span>
      <span className="text-zinc-600">·</span>
      <span className="font-mono text-zinc-200" data-testid="pipeline-sum-total">{eur0(total)}</span>
      <span className="text-zinc-500">netto</span>
      <span className="text-zinc-600">·</span>
      <span>davon gewonnen <b className="font-mono text-[#00E575]" data-testid="pipeline-sum-won">{eur0(won)}</b></span>
    </span>
  );
}

// Vertriebler-Auswahl: nur sichtbar, wenn mehrere Vertriebler in der Liste stehen.
function OwnerSelect({ projects, value, onChange }) {
  const owners = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => {
      const id = ownerKey(p);
      const cur = m.get(id) || { id, name: p.owner_name || "Ohne Vertriebler", n: 0 };
      cur.n += 1;
      m.set(id, cur);
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [projects]);
  if (ownerCount(projects) < 2) return null;
  return (
    <div className="relative" data-testid="pipeline-owner-filter">
      <UserRound size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${value ? "text-[#09090B]" : "text-zinc-400"}`} />
      <select
        value={value}
        onChange={(e) => { onChange(e.target.value); e.target.blur(); }}
        data-testid="pipeline-owner-select"
        style={{ backgroundImage: "none" }}
        aria-label="Vertriebler auswählen"
        className={`input-raw h-7 pl-7 pr-7 rounded-full text-[11.5px] font-semibold border appearance-none cursor-pointer transition-colors focus:outline-none ${
          value ? "bg-[#00E575] text-[#09090B] border-[#00E575]" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500"
        }`}
      >
        <option value="">Alle Vertriebler</option>
        {owners.map((o) => (
          <option key={o.id} value={o.id} data-testid={`pipeline-owner-option-${o.id}`}>{o.name} ({o.n})</option>
        ))}
      </select>
      <ChevronDown size={13} className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${value ? "text-[#09090B]" : "text-zinc-400"}`} />
    </div>
  );
}

function SortHeader({ col, sortKey, sortDir, onSort }) {
  const isActive = sortKey === col.id;
  const Icon = !isActive ? ArrowUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <button
      type="button"
      onClick={() => onSort(col.id)}
      data-testid={`pipeline-sort-${col.id}`}
      className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest font-semibold
                  hover:text-zinc-200 transition ${isActive ? "text-[#00E575]" : "text-zinc-500"}
                  ${col.align === "right" ? "justify-end w-full" : ""}`}>
      {col.align === "right" && <Icon size={11}/>}
      <span>{col.label}</span>
      {col.align !== "right" && <Icon size={11}/>}
    </button>
  );
}

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
}

export function ProjectPipeline({ projects, showRanking = false }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState("all");
  const [owner, setOwner] = useState("");
  const [month, setMonth] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sortKey, setSortKey] = useState("updated");
  const [sortDir, setSortDir] = useState("desc");
  const inputRef = useRef(null);

  // Iter88zd (2026-02-19) — User-Feedback: Liste kurzhalten damit man
  // schnell an die Kacheln kommt. Max 5 Zeilen im collapsed State,
  // Volltext-Suche + Chips + Sortierung ersetzen die längere Ansicht.
  const PREVIEW_ROWS = 3;

  // Cmd+K / Ctrl+K → Fokus aufs Suchfeld
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === "Escape" && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        setQuery("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Iter88zbr — Drei-Klick-Zyklus pro Spalte: asc → desc → aus (Default-Sort).
  const handleSort = (colId) => {
    if (sortKey !== colId) { setSortKey(colId); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    // dritter Klick — Default „updated desc" wieder herstellen.
    setSortKey("updated");
    setSortDir("desc");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (projects || []).filter((p) => {
      if (!_matchesChip(p, chip)) return false;
      if (owner && ownerKey(p) !== owner) return false;
      if (month && monthKey(p) !== month) return false;
      if (!q) return true;
      const hay = [
        p.customer_name, p.customer_email, p.customer_phone,
        p.status, p.owner_name, p.rep_name, p.id?.slice(0, 8),
        p.customer_city, p.customer_zip, p.city, p.zip,
        p.notes, p.offer_number, p.project_number,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [projects, query, chip, owner, month]);

  const sorted = useMemo(() => {
    const getter = SORT_GETTERS[sortKey] || SORT_GETTERS.updated;
    const factor = sortDir === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      // Projekte ohne Vertriebler immer ans Ende
      if (sortKey === "owner" && (!va || !vb)) return (!va) - (!vb);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const visible = showAll ? sorted : sorted.slice(0, PREVIEW_ROWS);
  // Rangliste folgt nur der Monatsauswahl (nicht Chips/Suche/Vertriebler)
  const monthProjects = useMemo(
    () => (month ? (projects || []).filter((p) => monthKey(p) === month) : projects || []),
    [projects, month],
  );
  const multiOwner = useMemo(() => ownerCount(projects) > 1, [projects]);
  const columns = multiOwner ? SORT_COLUMNS : SORT_COLUMNS.filter((c) => c.id !== "owner");
  const grid = multiOwner ? GRID : GRID_SINGLE;

  return (
    <section className="mb-8" data-testid="unified-pipeline">
      {/* Suchleiste + Neu-Button */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            ref={inputRef}
            data-testid="pipeline-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kunde, Ort, Status suchen …"
            className="w-full h-11 sm:h-10 bg-[#18181B] border border-zinc-800 rounded-xl pl-9 pr-16 text-[16px] sm:text-[13.5px]
                       focus:border-[#00E575] focus:outline-none placeholder:text-zinc-600"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 font-mono
                          bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 hidden sm:inline">
            ⌘K
          </kbd>
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="pipeline-search-clear"
              className="absolute right-12 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
            ><X size={14}/></button>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setAddOpen((v) => !v)}
            data-testid="pipeline-new-toggle"
            className="h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[12px]
                       uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-[#00C964]">
            <Plus size={14}/> Neu
          </button>
          {addOpen && (
            <div className="absolute right-0 mt-1 min-w-[180px] bg-[#18181B] border border-zinc-800 rounded-xl
                            shadow-xl z-30 overflow-hidden">
              <button
                onClick={() => { setAddOpen(false); navigate("/projects/new?type=wp"); }}
                data-testid="pipeline-new-wp"
                className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-zinc-800 inline-flex items-center gap-2">
                <Flame size={14} className="text-orange-400"/> Neues WP-Projekt
              </button>
              <button
                onClick={() => { setAddOpen(false); navigate("/projects/new?type=pv"); }}
                data-testid="pipeline-new-pv"
                className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-zinc-800 inline-flex items-center gap-2">
                <Sun size={14} className="text-amber-300"/> Neues PV-Projekt
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chips */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="pipeline-chips">
        {CHIPS.map((c) => {
          const active = chip === c.id;
          return (
            <button key={c.id}
              onClick={() => setChip(c.id)}
              data-testid={`pipeline-chip-${c.id}`}
              className={`h-9 sm:h-7 px-3 rounded-full text-[12.5px] sm:text-[11.5px] font-semibold border transition ${
                active
                  ? "bg-[#00E575] text-[#09090B] border-[#00E575]"
                  : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500"
              }`}>
              {c.label}
            </button>
          );
        })}
        <OwnerSelect projects={projects} value={owner} onChange={setOwner} />
        <MonthSelect projects={projects} value={month} onChange={setMonth} />
        <PipelineTotals rows={sorted} ownerName={owner ? ownerLabel(projects, owner) : ""} />
      </div>

      {/* Tabelle */}
      {visible.length === 0 ? (
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-8 text-center text-[13px] text-zinc-500">
          {query ? "Keine Treffer – Suche anpassen oder Filter erweitern." : "Keine Projekte in diesem Filter."}
        </div>
      ) : (
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl overflow-hidden" data-testid="pipeline-table">
          {/* Header mit sortierbaren Spalten */}
          <div className={`hidden sm:grid ${grid} gap-3 px-4 py-2.5 border-b border-zinc-800`}>
            {columns.map((col) => (
              <div key={col.id} className={col.align === "right" ? "text-right" : ""}>
                <SortHeader col={col} sortKey={sortKey} sortDir={sortDir} onSort={handleSort}/>
              </div>
            ))}
            <div/>
          </div>
          {visible.map((p) => {
            const isPv = _isPv(p) && !_isWp(p);
            const city = p.customer_city || p.city || "";
            const zip  = p.customer_zip  || p.zip  || "";
            const locLine = (city || zip) ? `${zip} ${city}`.trim() : (p.customer_email || p.customer_phone || "");
            return (
              <Link key={p.id}
                to={isPv ? `/projects/${p.id}/pv` : `/projects/${p.id}`}
                data-testid={`pipeline-row-${p.id}`}
                className={`block sm:grid ${grid} gap-3 px-3 sm:px-4 py-3 border-b border-zinc-800
                           last:border-b-0 hover:bg-zinc-800/40 transition items-center active:bg-zinc-800/60`}>
                <div className="sm:hidden" data-testid={`pipeline-row-mobile-${p.id}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-[14.5px] truncate">{p.customer_name || "—"}</span>
                    <span className="font-mono text-[13.5px] font-bold shrink-0">{p.offers_total_net ? eur0(p.offers_total_net) : "—"}</span>
                  </div>
                  <div className="text-[12px] text-zinc-500 truncate mt-0.5">
                    {locLine || "—"}{multiOwner && p.owner_name ? ` · ${p.owner_name}` : ""}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
                    <span className={`px-2 py-0.5 rounded font-semibold truncate max-w-[55%] ${STATUS_COLORS[p.status] || "bg-zinc-700"}`}>{p.status || "—"}</span>
                    {_isWp(p) && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/40 text-orange-300 font-mono"><Flame size={10}/> WP</span>}
                    {_isPv(p) && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-400/15 border border-amber-400/40 text-amber-200 font-mono"><Sun size={10}/> PV</span>}
                    <span className="ml-auto font-mono text-zinc-500">{_fmtDate(p.updated_at || p.created_at)}</span>
                  </div>
                </div>
                <div className="hidden sm:contents">
                <div className="min-w-0">
                  <div className="font-semibold text-[13.5px] truncate">{p.customer_name || "—"}</div>
                  <div className="text-[11.5px] text-zinc-500 truncate">{locLine || "—"}</div>
                </div>
                {multiOwner && (
                  <div className="min-w-0 text-[12px] text-zinc-300 truncate" data-testid={`pipeline-owner-${p.id}`}>
                    <span className="sm:hidden text-zinc-500">Vertriebler: </span>{p.owner_name || "—"}
                  </div>
                )}
                <div>
                  <span className={`inline-block px-2 py-0.5 rounded text-[10.5px] font-semibold ${STATUS_COLORS[p.status] || "bg-zinc-700"}`}>
                    {p.status || "—"}
                  </span>
                </div>
                <div className="text-[11.5px] font-mono">
                  <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`pipeline-type-${p.id}`}>
                    {_isWp(p) && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/40 text-orange-300"><Flame size={11}/> WP</span>}
                    {_isPv(p) && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-400/15 border border-amber-400/40 text-amber-200"><Sun size={11}/> PV</span>}
                  </span>
                </div>
                <div className="text-right font-mono text-[12.5px]" data-testid={`pipeline-amount-${p.id}`}>
                  {p.offers_total_net ? eur0(p.offers_total_net) : "—"}
                </div>
                <div className="text-right font-mono text-[11.5px] text-zinc-400">
                  {_fmtDate(p.updated_at || p.created_at)}
                </div>
                <div className="text-right hidden sm:block"><ArrowRight size={14} className="text-zinc-600"/></div>
                </div>
              </Link>
            );
          })}
          {sorted.length > PREVIEW_ROWS && (
            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="pipeline-toggle-all"
              className="w-full py-3 text-[12px] text-zinc-400 hover:text-[#00E575] hover:bg-zinc-800/40 border-t border-zinc-800">
              {showAll ? "Weniger zeigen" : `Alle ${sorted.length} anzeigen`}
            </button>
          )}
        </div>
      )}
      {showRanking && <RepRanking projects={monthProjects} month={month} />}
    </section>
  );
}

export default ProjectPipeline;
